/**
 * WhatsDuePanel · Jun 2026 — due-date nudges from the What's Due tile.
 *
 * Pops open from the red count badge on the Launch/Operating command
 * bars. Three sections, all actionable WITHOUT leaving the page:
 *   • Compliance — overdue + due-this-week open items:
 *       Mark done · Snooze 1w · inline Reschedule date picker
 *   • Roadmap — open tasks due this week / overdue:
 *       Mark done · Push 1w (shifts start + completion dates a week out)
 *   • Grants — saved/drafting applications closing this week:
 *       Mark submitted · inline deadline date picker
 *
 * Clearing the last due item fires a small confetti celebration.
 */
import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { AlarmClockPlus, CalendarClock, Check, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/apiClient";
import {
  complianceOverdueItems,
  complianceDueSoonItems,
  roadmapOverdueItems,
  roadmapDueSoonItems,
  grantsDueSoonItems,
} from "../DueSoonBanner";

const plusDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function WhatsDuePanel({ business, businessId, onClose, onBusinessUpdate, positionClass = "" }) {
  const [busyId, setBusyId] = useState(null);
  const compRows = [
    ...complianceOverdueItems(business).map((it) => ({ ...it, _overdue: true })),
    ...complianceDueSoonItems(business),
  ];
  const roadRows = [
    ...roadmapOverdueItems(business).map((it) => ({ ...it, _overdue: true })),
    ...roadmapDueSoonItems(business),
  ];
  const grantRows = grantsDueSoonItems(business);
  const total = compRows.length + roadRows.length + grantRows.length;

  // Jun 2026 · Celebrate a clear week — when the last due item is
  // cleared while the panel is open, fire a small confetti burst.
  const prevTotalRef = useRef(total);
  useEffect(() => {
    if (prevTotalRef.current > 0 && total === 0) {
      confetti({ particleCount: 110, spread: 75, origin: { y: 0.75 } });
      toast.success("Clear week — everything due is handled. Nice work!");
    }
    prevTotalRef.current = total;
  }, [total]);

  const run = async (itemId, fn, successMsg) => {
    setBusyId(itemId);
    try {
      await fn();
      toast.success(successMsg);
      onBusinessUpdate?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "object" && detail?.message) ||
        (typeof detail === "string" && detail) ||
        "Couldn't update the item. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const patchCompliance = (item, body, msg) =>
    run(item.id, () => api.patch(`/businesses/${businessId}/compliance/${item.id}`, body), msg);

  const patchRoadmap = (item, body, msg) =>
    run(item.id, () => api.patch(`/businesses/${businessId}/roadmap/${item.id}`, body), msg);

  const patchGrant = (item, body, msg) =>
    run(item.id, () => api.patch(`/businesses/${businessId}/grants/${item.id}`, body), msg);

  const snoozeCompliance = (item) => {
    const base = item.due_date && item.due_date > todayIso() ? item.due_date : todayIso();
    patchCompliance(item, { due_date: plusDays(base, 7) }, "Snoozed 1 week — due date moved.");
  };

  const pushRoadmap = (item) => {
    const base = item.completion_date && item.completion_date > todayIso() ? item.completion_date : todayIso();
    const body = { completion_date: plusDays(base, 7) };
    if (item.start_date) body.start_date = plusDays(item.start_date, 7);
    patchRoadmap(item, body, "Pushed 1 week — task dates moved.");
  };

  const actionBtnClass =
    "inline-flex items-center gap-1 px-2 py-1 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white text-[10px] font-black uppercase tracking-[0.12em] transition-colors disabled:opacity-50";

  const renderDate = (it, prefix, dateVal) => (
    <span
      className={`text-[10px] font-black tabular-nums whitespace-nowrap ${it._overdue ? "text-[#DC2626]" : "text-neutral-600"}`}
      data-testid={`whats-due-item-date-${it.id}`}
    >
      {it._overdue ? "Overdue · " : `${prefix} · `}{dateVal}
    </span>
  );

  return (
    <div
      className={`absolute w-96 max-w-[calc(100vw-2rem)] border border-[#4A4458] bg-white shadow-[4px_4px_0px_rgba(74,68,88,1)] p-4 text-left ${positionClass}`}
      style={{ borderRadius: 0 }}
      data-testid="whats-due-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-[#DC2626]">
          What's due · {total} item{total === 1 ? "" : "s"}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
          data-testid="whats-due-panel-close"
          aria-label="Close what's due panel"
        >
          <X className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>
      {total === 0 ? (
        <p className="mt-3 text-xs text-neutral-600" data-testid="whats-due-panel-empty">
          Nothing due or overdue — you're all caught up. 🎉
        </p>
      ) : (
        <div className="mt-3 space-y-3 max-h-80 overflow-y-auto pr-1">
          {compRows.length > 0 && (
            <div data-testid="whats-due-compliance-section">
              <div className="text-[9px] font-black uppercase tracking-[0.2em] text-neutral-500">
                Compliance
              </div>
              <ul className="mt-1.5 space-y-2.5">
                {compRows.map((it) => (
                  <li
                    key={it.id}
                    className="border border-neutral-200 p-2.5"
                    data-testid={`whats-due-item-${it.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-bold text-[#4A4458] leading-snug">{it.title}</span>
                      {renderDate(it, "Due", it.due_date)}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() =>
                          patchCompliance(it, { status: "completed" }, "Marked done — nice work.")
                        }
                        disabled={busyId === it.id}
                        className={`${actionBtnClass} border-emerald-700 text-emerald-700 hover:bg-emerald-700`}
                        data-testid={`whats-due-done-${it.id}`}
                      >
                        <Check className="w-3 h-3" aria-hidden /> Done
                      </button>
                      <button
                        type="button"
                        onClick={() => snoozeCompliance(it)}
                        disabled={busyId === it.id}
                        className={actionBtnClass}
                        data-testid={`whats-due-snooze-${it.id}`}
                      >
                        <AlarmClockPlus className="w-3 h-3" aria-hidden /> Snooze 1w
                      </button>
                      <label className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-600">
                        Reschedule
                        <input
                          type="date"
                          defaultValue={it.due_date || ""}
                          disabled={busyId === it.id}
                          onChange={(e) => {
                            if (e.target.value && e.target.value !== it.due_date) {
                              patchCompliance(it, { due_date: e.target.value }, `Rescheduled to ${e.target.value}.`);
                            }
                          }}
                          className="border border-neutral-300 px-1.5 py-0.5 text-[11px] font-medium text-neutral-800 focus:border-neutral-900 outline-none"
                          style={{ borderRadius: 0 }}
                          data-testid={`whats-due-reschedule-${it.id}`}
                        />
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {roadRows.length > 0 && (
            <div data-testid="whats-due-roadmap-section">
              <div className="text-[9px] font-black uppercase tracking-[0.2em] text-neutral-500">
                Roadmap
              </div>
              <ul className="mt-1.5 space-y-2.5">
                {roadRows.map((it) => (
                  <li
                    key={it.id}
                    className="border border-neutral-200 p-2.5"
                    data-testid={`whats-due-roadmap-item-${it.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-bold text-[#4A4458] leading-snug">{it.title}</span>
                      {renderDate(it, "Ship", it.completion_date)}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() =>
                          patchRoadmap(it, { status: "done" }, "Task shipped — marked done.")
                        }
                        disabled={busyId === it.id}
                        className={`${actionBtnClass} border-emerald-700 text-emerald-700 hover:bg-emerald-700`}
                        data-testid={`whats-due-roadmap-done-${it.id}`}
                      >
                        <Check className="w-3 h-3" aria-hidden /> Done
                      </button>
                      <button
                        type="button"
                        onClick={() => pushRoadmap(it)}
                        disabled={busyId === it.id}
                        className={actionBtnClass}
                        data-testid={`whats-due-roadmap-push-${it.id}`}
                      >
                        <CalendarClock className="w-3 h-3" aria-hidden /> Push 1w
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {grantRows.length > 0 && (
            <div data-testid="whats-due-grants-section">
              <div className="text-[9px] font-black uppercase tracking-[0.2em] text-neutral-500">
                Grants
              </div>
              <ul className="mt-1.5 space-y-2.5">
                {grantRows.map((it) => (
                  <li
                    key={it.id}
                    className="border border-neutral-200 p-2.5"
                    data-testid={`whats-due-grant-item-${it.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-bold text-[#4A4458] leading-snug">{it.title}</span>
                      <span
                        className="text-[10px] font-black tabular-nums whitespace-nowrap text-neutral-600"
                        data-testid={`whats-due-item-date-${it.id}`}
                      >
                        Closes · {it.deadline}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() =>
                          patchGrant(it, { status: "submitted" }, "Marked submitted — fingers crossed.")
                        }
                        disabled={busyId === it.id}
                        className={`${actionBtnClass} border-emerald-700 text-emerald-700 hover:bg-emerald-700`}
                        data-testid={`whats-due-grant-submitted-${it.id}`}
                      >
                        <Send className="w-3 h-3" aria-hidden /> Submitted
                      </button>
                      <label className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-600">
                        Deadline
                        <input
                          type="date"
                          defaultValue={it.deadline || ""}
                          disabled={busyId === it.id}
                          onChange={(e) => {
                            if (e.target.value && e.target.value !== it.deadline) {
                              patchGrant(it, { deadline: e.target.value }, `Deadline updated to ${e.target.value}.`);
                            }
                          }}
                          className="border border-neutral-300 px-1.5 py-0.5 text-[11px] font-medium text-neutral-800 focus:border-neutral-900 outline-none"
                          style={{ borderRadius: 0 }}
                          data-testid={`whats-due-grant-deadline-${it.id}`}
                        />
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
